/**
 * Доступ к запланированным приёмам лекарств.
 *
 * Лента «сегодня» читает один день по индексу `[userId+date]`, карточки курсов —
 * отрезок дат тем же индексом. Полных переборов таблицы здесь нет: приёмов
 * накапливается по несколько штук в день, за годы это десятки тысяч записей.
 */

import { useLiveQuery } from 'dexie-react-hooks';

import { db } from '@/shared/db/database';
import type { DoseEvent, SkipReason } from '@/shared/db/schema';
import { getCurrentUserId } from '@/shared/db/session-context';
import type { IsoDate } from '@/shared/lib/datetime';

import { doseEventsRepo } from './repository';

/** Сортировка приёмов внутри дня: по времени, затем по препарату. */
function byTime(a: DoseEvent, b: DoseEvent): number {
  if (a.scheduledMin !== b.scheduledMin) return a.scheduledMin - b.scheduledMin;
  return a.medicationId.localeCompare(b.medicationId);
}

export async function queryDoses(from: IsoDate, to: IsoDate): Promise<DoseEvent[]> {
  const userId = getCurrentUserId();
  if (!userId) return [];

  const doses = await db.doseEvents
    .where('[userId+date]')
    .between([userId, from], [userId, to], true, true)
    .toArray();

  return doses
    .filter((dose) => dose.deletedAt === null)
    .sort((a, b) => (a.date === b.date ? byTime(a, b) : a.date.localeCompare(b.date)));
}

export function useDosesForDate(date: IsoDate): DoseEvent[] | undefined {
  return useLiveQuery(() => queryDoses(date, date), [date]);
}

export function useDosesInRange(from: IsoDate, to: IsoDate): DoseEvent[] | undefined {
  return useLiveQuery(() => queryDoses(from, to), [from, to]);
}

/** Все приёмы конкретного препарата — журнал курса. */
export async function queryDosesForMedication(medicationId: string): Promise<DoseEvent[]> {
  const doses = await db.doseEvents
    .where('[medicationId+date]')
    .between([medicationId, ''], [medicationId, '￿'], true, true)
    .toArray();

  return doses.filter((dose) => dose.deletedAt === null).sort(byTime);
}

/* ─────────────────────── Отметки приёма ─────────────────────── */

/**
 * «Принял».
 *
 * `actualAmount` хранится отдельно от планового: приняли половину таблетки —
 * это другой факт, чем плановый приём, и при разборе с врачом разница важна.
 *
 * `takenAt` можно задать явно — это нужно, когда приём отмечают задним числом:
 * человек выпил таблетку вечером, а вспомнил об этом утром, и в журнале должно
 * стоять настоящее время, а не момент нажатия кнопки. Иначе журнал приёмов
 * врёт именно там, где его и показывают врачу.
 */
export async function markDoseTaken(
  dose: DoseEvent,
  options: { actualAmount?: number; takenAt?: number } = {},
): Promise<void> {
  await doseEventsRepo.update(dose.id, {
    status: 'taken',
    takenAt: options.takenAt ?? Date.now(),
    actualAmount: options.actualAmount ?? dose.plannedAmount,
    snoozedUntil: null,
    skipReason: null,
  });
}

export async function markDoseSkipped(dose: DoseEvent, reason: SkipReason): Promise<void> {
  await doseEventsRepo.update(dose.id, {
    status: 'skipped',
    takenAt: null,
    actualAmount: null,
    snoozedUntil: null,
    skipReason: reason,
  });
}

/**
 * «Отложить»: приём не исчезает, а переезжает на указанное число минут.
 * Плановое время не меняется — иначе журнал перестал бы отражать назначение.
 */
export async function postponeDose(dose: DoseEvent, minutes: number): Promise<void> {
  await doseEventsRepo.update(dose.id, {
    status: 'postponed',
    snoozedUntil: Date.now() + minutes * 60_000,
  });
}

/**
 * Перенос приёма на выбранное время того же дня.
 *
 * Тот же механизм, что и «отложить», только час называет человек. Плановое
 * время снова не трогается: расписание курса — назначение, а перенос — про
 * один сегодняшний день. Именно поэтому перенесённый приём переживает любую
 * пересборку расписания: для планировщика он остался на своём месте.
 */
export async function rescheduleDose(dose: DoseEvent, at: number): Promise<void> {
  await doseEventsRepo.update(dose.id, { status: 'postponed', snoozedUntil: at });
}

/** Возврат приёма в исходное состояние — отмена ошибочной отметки. */
export async function resetDose(dose: DoseEvent): Promise<void> {
  await doseEventsRepo.update(dose.id, {
    status: 'pending',
    takenAt: null,
    actualAmount: null,
    snoozedUntil: null,
    skipReason: null,
  });
}

/**
 * Намеченный приём курса «по необходимости».
 *
 * Расписания у такого курса нет и быть не должно — но напоминание нужно:
 * «сегодня выпью в девять вечера». Напоминания в приложении строятся из
 * приёмов, поэтому намеченный приём — обычная запись со статусом `pending`,
 * просто заведена человеком, а не планировщиком.
 *
 * Планировщик её не тронет: курсы «по необходимости» он обходит стороной.
 */
export async function logPlannedDose(
  medicationId: string,
  date: IsoDate,
  scheduledMin: number,
  amount: number,
): Promise<void> {
  await doseEventsRepo.create({
    medicationId,
    date,
    // Приём назначен человеком на час, а не посчитан от еды.
    mealRoutineId: null,
    scheduledMin,
    plannedAmount: amount,
    status: 'pending',
    takenAt: null,
    actualAmount: null,
    snoozedUntil: null,
    skipReason: null,
    note: '',
  });
}

/**
 * Разовый приём вне расписания.
 *
 * Нужен и препаратам «по необходимости», и внеплановой таблетке в обычном
 * курсе: врач добавил приём, стало хуже, забыли утром и приняли в обед. Такой
 * приём заводится отдельной записью и не трогает расписание — иначе разовое
 * событие меняло бы назначение.
 *
 * `takenAt` задаётся явно, когда время выбрано вручную: без этого журнал
 * показывал бы момент нажатия кнопки вместо настоящего приёма.
 */
export async function logExtraDose(
  medicationId: string,
  date: IsoDate,
  scheduledMin: number,
  amount: number,
  takenAt?: number,
): Promise<void> {
  await doseEventsRepo.create({
    medicationId,
    date,
    /*
      Связи с едой у внепланового приёма нет, и это важно: планировщик сверяет
      расписание по ней. Появись здесь ссылка на завтрак — выпитая сверх плана
      таблетка выдала бы себя за плановую и отменила настоящий приём.
    */
    mealRoutineId: null,
    scheduledMin,
    plannedAmount: amount,
    status: 'taken',
    takenAt: takenAt ?? Date.now(),
    actualAmount: amount,
    snoozedUntil: null,
    skipReason: null,
    note: '',
  });
}
